import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../hooks/useAuth';

const Notifications = () => {
    const { user } = useAuth();
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications
    } = useNotifications(user);

    const [isOpen, setIsOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const dropdownRef = useRef(null);

    // Cerrar dropdown al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Formatear fecha relativa
    const formatRelativeTime = (date) => {
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));

        if (diffInMinutes < 1) return 'Ahora mismo';
        if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `Hace ${diffInHours}h`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `Hace ${diffInDays}d`;

        return date.toLocaleDateString();
    };

    // Obtener notificaciones recientes (últimas 5)
    const recentNotifications = notifications.slice(0, 5);
    const displayNotifications = showAll ? notifications : recentNotifications;

    // Manejar click en notificación
    const handleNotificationClick = async (notification) => {
        if (!notification.leida) {
            await markAsRead(notification.id);
        }

        // Aquí podríamos agregar navegación específica según el tipo
        // Por ejemplo, redirigir a un plan específico, post, etc.
        setIsOpen(false);
    };

    if (loading) {
        return (
            <div className="notification-bell">
                <button className="notification-btn">
                    <span className="bell-icon">🔔</span>
                </button>
            </div>
        );
    }

    return (
        <div className="notification-bell" ref={dropdownRef}>
            <button
                className="notification-btn"
                onClick={() => setIsOpen(!isOpen)}
                title="Notificaciones"
            >
                <span className="bell-icon">🔔</span>
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h4>Notificaciones</h4>
                        <div className="notification-actions">
                            {unreadCount > 0 && (
                                <button
                                    className="mark-all-read-btn"
                                    onClick={markAllAsRead}
                                >
                                    Marcar todas como leídas
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button
                                    className="clear-all-btn"
                                    onClick={clearAllNotifications}
                                >
                                    Limpiar todas
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="notification-list">
                        {displayNotifications.length === 0 ? (
                            <div className="no-notifications">
                                <span className="no-notifications-icon">📭</span>
                                <p>No tienes notificaciones</p>
                            </div>
                        ) : (
                            displayNotifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.leida ? 'unread' : ''}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-icon">
                                        {notification.icono || '📢'}
                                    </div>

                                    <div className="notification-content">
                                        <div className="notification-title">
                                            {notification.titulo}
                                        </div>
                                        <div className="notification-message">
                                            {notification.mensaje}
                                        </div>
                                        <div className="notification-time">
                                            {formatRelativeTime(notification.fechaCreacion)}
                                        </div>
                                    </div>

                                    <div className="notification-actions">
                                        {!notification.leida && (
                                            <div className="unread-indicator"></div>
                                        )}
                                        <button
                                            className="delete-notification-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteNotification(notification.id);
                                            }}
                                            title="Eliminar notificación"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {notifications.length > 5 && (
                        <div className="notification-footer">
                            <button
                                className="view-all-btn"
                                onClick={() => setShowAll(!showAll)}
                            >
                                {showAll ? 'Ver menos' : `Ver todas (${notifications.length})`}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Notifications;